// The daily exam's rule of the day. Everyone gets the same rule and the same
// opponents on a date: the opponents come from the date seed, the rule from
// a shuffle of all rules per week (seeded by its Monday), so a week never
// repeats a rule and no rule comes twice in a row.
//
// Rules are data: a mix, a level and count change, sim options, a stage.
// New rules join with `since` (from the first Monday on or after it), so past
// days keep theirs. Never change or remove a rule: shared links name a day's
// rule by its code. Names and descriptions are 'rule.<id>.name/.desc'.

import { dateSeed, mulberry32 } from '@/core/rng';
import { dayNumber, isoFromDay, weekday } from '@/core/dates';
import type { RoundSpec, SimOptions } from '@/sim/sim';
import type { BossId, EnemyKind } from '@/sim/tuning';
import type { StageId } from '@/render/stages';
import { DAILY_ROUNDS } from './exams';

export type RuleId =
  | 'heavy'
  | 'feint'
  | 'boxing'
  | 'kick'
  | 'oneHeart'
  | 'kiai'
  | 'night'
  | 'speed'
  | 'rhythm'
  | 'boss';

export interface DailyRule {
  id: RuleId;
  /** One URL-safe character naming the rule in challenge links. */
  code: string;
  /** Spawn weights for every round, instead of the daily default mix. */
  kinds?: Partial<Record<EnemyKind, number>>;
  /** Added to every round's level and opponent count. */
  level?: number;
  count?: number;
  /** Share of arrivals that are attack patterns, instead of the daily default. */
  patterns?: number;
  /** A stage boss who ends the last round. */
  boss?: BossId;
  sim?: SimOptions;
  /** Where it's fought; the park by default. */
  stage?: StageId;
  /** The opponent the day is about, pictured on the intro card. */
  features?: EnemyKind;
  /** ISO date the rule joins the rotation (from the next Monday on). */
  since?: string;
}

export const RULES: readonly DailyRule[] = [
  { id: 'heavy', code: 'h', kinds: { basic: 2, heavy: 3 }, features: 'heavy' },
  { id: 'feint', code: 'f', kinds: { basic: 2, heavy: 1, feinter: 3 }, features: 'feinter' },
  { id: 'boxing', code: 'b', kinds: { basic: 2, feinter: 1, blocker: 3 }, features: 'blocker' },
  // Kickers normally wait for purple: a level slower for the white belts
  {
    id: 'kick',
    code: 'k',
    kinds: { basic: 3, heavy: 1, kicker: 3 },
    level: -1,
    features: 'kicker',
  },
  { id: 'oneHeart', code: '1', level: -3, count: -4, sim: { hearts: 1 } },
  { id: 'kiai', code: 'i', sim: { kiaiRate: 2 } },
  { id: 'night', code: 'n', stage: 'rooftop' },
  { id: 'speed', code: 's', level: 2, count: -3 },
  { id: 'rhythm', code: 'r', patterns: 0.7, since: '2026-10-12' },
  { id: 'boss', code: 'x', boss: 'captain', since: '2026-10-12' },
];

/** The week's rules in shuffled order, Monday first. */
function shuffled(monday: number): DailyRule[] {
  const pool = RULES.filter((r) => !r.since || dayNumber(r.since) <= monday);
  const rng = mulberry32(dateSeed(`kiai-rules-${isoFromDay(monday)}`));
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  return pool;
}

/** A week's rules; Monday never repeats the previous Sunday. Only slots 0
 *  and 1 are ever swapped, so a week's Sunday is its raw shuffle's. */
function week(monday: number): DailyRule[] {
  const rules = shuffled(monday);
  if (rules[0] === shuffled(monday - 7)[6]) [rules[0], rules[1]] = [rules[1]!, rules[0]!];
  return rules;
}

export function ruleFor(date: string): DailyRule {
  const day = weekday(date);
  return week(dayNumber(date) - day)[day]!;
}

const cache = new Map<RuleId, readonly RoundSpec[]>();

/** The daily exam's rounds under a rule. */
export function dailyRounds(rule: DailyRule): readonly RoundSpec[] {
  let rounds = cache.get(rule.id);
  if (!rounds) {
    rounds = DAILY_ROUNDS.map((r, i) => ({
      ...r,
      ...(rule.boss && i === DAILY_ROUNDS.length - 1 ? { boss: rule.boss } : {}),
      level: Math.max(0, r.level + (rule.level ?? 0)),
      count: Math.max(4, (r.count ?? 0) + (rule.count ?? 0)),
      ...(rule.kinds ? { kinds: rule.kinds } : {}),
      ...(rule.patterns !== undefined ? { patterns: rule.patterns } : {}),
    }));
    cache.set(rule.id, rounds);
  }
  return rounds;
}
